import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { createAgentTarget } from "@/modules/targets/api/index.v2";
import {
  TargetsFormData,
  useTargetsTemplateContext,
} from "@/modules/targets/context/targets-template.context";
import { TargetTypes } from "@/modules/targets/types";
import {
  getCurrentQuarterMonths,
  generateTargetPeriods,
} from "@/modules/targets/utils";
import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

interface UseCreateAgentTargetTemplateProps {
  onSuccess?: () => void;
}

const useCreateAgentTargetTemplate = ({
  onSuccess,
}: UseCreateAgentTargetTemplateProps = {}) => {
  const { uid } = useParams();

  const { mutate, isLoading } = useMutation({
    mutationFn: createAgentTarget,
    onSuccess: () => {
      showToast({
        type: ToastType.SUCCESS,
        message: "Agent target created successfully",
        toastId: TOAST_IDS.CREATE_AGENT_TARGET_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
      // Call the onSuccess callback if provided
      onSuccess?.();
    },
    onError: () => {
      showToast({
        type: ToastType.ERROR,
        message: "Agent target creation failed",
        toastId: TOAST_IDS.CREATE_AGENT_TARGET_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  const { form, subDepartment, validateForm } = useTargetsTemplateContext();

  const { handleSubmit } = form;

  const onSubmit = (data: TargetsFormData) => {
    const { isValid, errors } = validateForm();

    if (!isValid) {
      errors.forEach((error) => {
        showToast({
          type: ToastType.ERROR,
          message: error,
          toastId: `validation-error-${Date.now()}-${crypto.randomUUID()}`,
          options: {
            position: "bottom-left",
            autoClose: 5000,
          },
        });
      });
      return;
    }

    const currentQuarter = getCurrentQuarterMonths();

    const payload = data.ranksTargets.map((rankTarget) => ({
      subDepartmentUId: subDepartment?.uid,
      organizationUIds: [uid ?? ""],
      rankUId: rankTarget.rankUId,
      targetType: TargetTypes.GMV,
      targetPeriods: generateTargetPeriods(
        currentQuarter,
        rankTarget.personalTarget,
        rankTarget.minimumTarget
      ),
    }));

    mutate(payload);
  };

  const submitForm = handleSubmit(onSubmit);

  return {
    isLoading,
    submitCreateAgentTargetTemplate: submitForm,
  };
};

export default useCreateAgentTargetTemplate;
